import React from 'react'
import {
  PdfViewerComponent,
  Magnification,
  Navigation,
  LinkAnnotation,
  BookmarkView,
  Annotation,
  FormFields,
  FormDesigner,
  ThumbnailView,
  Print,
  TextSelection,
  TextSearch,
  PageOrganizer,
  Inject,
} from '@syncfusion/ej2-react-pdfviewer'
import {
  ToolbarComponent,
  ItemsDirective,
  ItemDirective,
} from '@syncfusion/ej2-react-navigations'
import { useMediaQuery } from '@material-ui/core'

import './pdf-viewer.css'

/**
 * @typedef {object} Props
 * @prop {string} url Sets the PDF document URL.
 * @prop {Function} handleDocumentLoadFailed Custom function to handle when document cannot load.
 * @prop {import('i18next').TFunction} t Translation function
 * @prop {boolean} isExtended Handles a viewer greater height
 */

/**
 * @author Marissa Vargas
 * @date 2025-05-15
 * @description Component to view pdf's from SyncFusion, with a customized toolbar. Enables to navigate among pages, zoom, drag, select text and perfomr text search. It dynamically changes the tools for devices below 820 pexels.
 * @param {Props}
 * @returns {JSX.Element}
 * @example
 * ```jsx
 * <PdfViewer>
 *   url={'https://cdn.syncfusion.com/content/pdf/pdf-succinctly.pdf'}
 *   handleDocumentLoadFailed={(e) => setErrorMessage(e.message)}
 *   t={t}
 * </PdfViewer/>
 * ```
 */
function PdfViewer({ url, handleDocumentLoadFailed, t, isExtended }) {
  let viewer
  let matchCase
  let searchText = ''
  let prevMatchCase = false
  let toolbar
  let currentPageNumber = '1'
  let isInkEnabled = false
  let searchActive = false

  const is1024 = useMediaQuery('(max-width:1024px)')
  const is1820 = useMediaQuery('(min-width: 1820px)')
  const is1366 = useMediaQuery('(min-width: 1366px)')

  let viewerHeight = '640px'

  if (isExtended) {
    if (is1820) {
      viewerHeight = '1120px'
    } else if (is1366) {
      viewerHeight = '1119px'
    } else if (is1024) {
      viewerHeight = '1140px'
    }
  }

  function template() {
    return (
      <div style={{ margin: '0px 6px' }}>
        <span className='e-pv-total-page-number' id='totalPage'>
          of 0
        </span>
      </div>
    )
  }

  const handleCreated = () => {
    if (viewer) {
      viewer.dataBind()
      viewer.load(url, null)
      viewer.serviceUrl = ''
    }
  }

  if (viewer) viewer.serviceUrl = process.env.REACT_APP_SERVICE_URL_SYNCFUSION

  function inputTemplate() {
    return (
      <div>
        <input
          type='text'
          className='e-input-group e-pv-current-page-number'
          id='currentPage'
        />
      </div>
    )
  }

  return (
    <div>
      {is1024 ? (
        <div className='control-section'>
          <div>
            <div className='e-pdf-toolbar'>
              <ToolbarComponent
                ref={(scope) => {
                  toolbar = scope
                }}
                clicked={clickHandler.bind(this)}
              >
                <ItemsDirective>
                  <ItemDirective
                    prefixIcon='e-icons e-first-page'
                    id='first_page'
                    tooltipText={t('pdfViewer.tooltip.firstPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-chevron-left'
                    id='previous_page'
                    tooltipText={t('pdfViewer.tooltip.previousPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-chevron-right'
                    id='next_page'
                    tooltipText={t('pdfViewer.tooltip.nextPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-last-page'
                    id='last_page'
                    tooltipText={t('pdfViewer.tooltip.lastPage')}
                    align='Left'
                  />
                  <ItemDirective
                    template={inputTemplate}
                    tooltipText={t('pdfViewer.tooltip.pageNumber')}
                    type='Input'
                    align='Left'
                  />
                  <ItemDirective
                    template={template}
                    align='Left'
                    tooltipText={t('pdfViewer.tooltip.pageNumber')}
                  />

                  <ItemDirective type='Separator' />
                  <ItemDirective
                    prefixIcon='e-icons e-pan'
                    id='pan_tool'
                    tooltipText={t('pdfViewer.tooltip.panMode')}
                  />

                  <ItemDirective type='Separator' align='Center' />
                  <ItemDirective
                    prefixIcon='e-icons e-search'
                    tooltipText={t('pdfViewer.tooltip.findText')}
                    id='find_text'
                    align='Right'
                  />
                </ItemsDirective>
              </ToolbarComponent>
            </div>

            <div
              id='textSearchToolbar'
              style={{ display: 'none', marginLeft: '840px' }}
            >
              <div
                className='e-pv-search-bar'
                id='container_search_box'
              >
                <div
                  className='e-pv-search-bar-elements'
                  id='container_search_box_elements'
                >
                  <div
                    className='e-input-group e-pv-search-input'
                    id='container_search_input_container'
                  >
                    <input
                      className='e-input'
                      id='container_search_input'
                      type='text'
                      placeholder={t('pdfViewer.searchText.findInDocument')}
                      onKeyPress={searchInputKeypressed}
                      onChange={inputChange}
                    />
                    <span
                      className='e-input-group-icon e-input-search-group-icon e-icons e-search'
                      id='container_search_box-icon'
                      onClick={searchClickHandler}
                    ></span>
                    <span
                      className='e-pv-search-count-label'
                      id='container_search_count_label'
                    ></span>
                  </div>
                  <button
                    className='e-btn e-icon-btn e-pv-search-btn e-icons e-chevron-left'
                    id='container_prev_occurrence'
                    type='button'
                    disabled
                    aria-label='Previous Search text'
                  >
                    <span
                      className='e-pv-icon-search e-pv-prev-search-icon'
                      id='container_prev_occurrenceIcon'
                      onClick={previousTextSearch}
                    ></span>
                  </button>
                  <button
                    className='e-btn e-icon-btn e-pv-search-btn e-icons e-chevron-right'
                    id='container_next_occurrence'
                    type='button'
                    disabled
                    aria-label='Next Search text'
                  >
                    <span
                      className='e-pv-icon-search e-pv-next-search-icon'
                      id='container_next_occurrenceIcon'
                      onClick={nextTextSearch}
                    ></span>
                  </button>
                </div>
                <div
                  className='e-pv-match-case-container'
                  id='container_match_case_container'
                >
                  <div className='e-checkbox-wrapper e-wrapper e-pv-match-case'>
                    <label htmlFor='container_match_case'>
                      <input
                        id='container_match_case'
                        type='checkbox'
                        className='e-control e-checkbox e-lib'
                        onClick={checkBoxChanged}
                      />
                      <span
                        className='e-ripple-container'
                        data-ripple='true'
                      ></span>
                      <span id='checkboxSpan' className='e-icons e-frame'></span>
                      <span className='e-label'>
                        {t('pdfViewer.checkBox.matchCase')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            {/* Render the PDF Viewer */}
            <PdfViewerComponent
              id='container'
              ref={(scope) => {
                viewer = scope
              }}
              created={handleCreated}
              enableToolbar={false}
              enableNavigationToolbar={false}
              enableAnnotationToolbar={false}
              enableCommentPanel={false}
              documentLoad={documentLoaded}
              documentLoadFailed={handleDocumentLoadFailed}
              pageChange={onPageChange}
              resourceUrl={`${process.env.REACT_APP_RESOURCE_URL_PDF_VIEWER_SYNCFUSION}`}
              documentPath={url}
              style={{
                display: 'block',
                height: viewerHeight,
                touchAction: 'pan-x pan-y',
                minHeight: '500px'
              }}            >
              <Inject
                services={[
                  Magnification,
                  Navigation,
                  LinkAnnotation,
                  BookmarkView,
                  FormFields,
                  FormDesigner,
                  PageOrganizer,
                  ThumbnailView,
                  Print,
                  TextSelection,
                  TextSearch,
                  Annotation,
                ]}
              />
            </PdfViewerComponent>
          </div>
        </div>
      ) : (
        <div className='control-section'>
          <div>
            <div className='e-pdf-toolbar'>
              <ToolbarComponent
                ref={(scope) => {
                  toolbar = scope
                }}
                clicked={clickHandler.bind(this)}
              >
                <ItemsDirective>
                  <ItemDirective
                    prefixIcon='e-icons e-first-page'
                    id='first_page'
                    tooltipText={t('pdfViewer.tooltip.firstPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-chevron-left'
                    id='previous_page'
                    tooltipText={t('pdfViewer.tooltip.previousPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-chevron-right'
                    id='next_page'
                    tooltipText={t('pdfViewer.tooltip.nextPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-last-page'
                    id='last_page'
                    tooltipText={t('pdfViewer.tooltip.lastPage')}
                    align='Left'
                  />
                  <ItemDirective
                    template={inputTemplate}
                    tooltipText={t('pdfViewer.tooltip.pageNumber')}
                    type='Input'
                    align='Left'
                  />
                  <ItemDirective
                    template={template}
                    align='Left'
                    tooltipText={t('pdfViewer.tooltip.pageNumber')}
                  />

                  <ItemDirective type='Separator' align='Left' />
                  <ItemDirective
                    prefixIcon='e-icons e-circle-add'
                    id='zoom_in'
                    tooltipText={t('pdfViewer.tooltip.zoomIn')}
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-circle-remove'
                    id='zoom_out'
                    tooltipText={t('pdfViewer.tooltip.zoomOut')}
                  />
                  <ItemDirective type='Separator' />

                  <ItemDirective
                    prefixIcon='e-icons e-mouse-pointer'
                    id='text_selection_tool'
                    tooltipText={t('pdfViewer.tooltip.textSelect')}
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-pan'
                    id='pan_tool'
                    tooltipText={t('pdfViewer.tooltip.panMode')}
                  />
                  <ItemDirective type='Separator' />

                  <ItemDirective type='Separator' align='Center' />
                  <ItemDirective
                    prefixIcon='e-icons e-search'
                    tooltipText={t('pdfViewer.tooltip.findText')}
                    id='find_text'
                    align='Right'
                  />
                </ItemsDirective>
              </ToolbarComponent>
            </div>

            <div
              id='textSearchToolbar'
              style={{ display: 'none', marginLeft: '840px' }}
            >
              <div
                className='e-pv-search-bar'
                id='container_search_box'
              >
                <div
                  className='e-pv-search-bar-elements'
                  id='container_search_box_elements'
                >
                  <div
                    className='e-input-group e-pv-search-input'
                    id='container_search_input_container'
                  >
                    <input
                      className='e-input'
                      id='container_search_input'
                      type='text'
                      placeholder={t('pdfViewer.searchText.findInDocument')}
                      onKeyPress={searchInputKeypressed}
                      onChange={inputChange}
                    />


                    <span
                      className='e-input-group-icon e-input-search-group-icon e-icons e-search'
                      id='container_search_box-icon'
                      onClick={searchClickHandler}
                    ></span>

                    <span
                      className='e-pv-search-count-label'
                      id='container_search_count_label'

                    ></span>
                  </div>
                  <button
                    className='e-btn e-icon-btn e-pv-search-btn e-icons e-chevron-left'
                    id='container_prev_occurrence'
                    type='button'
                    disabled
                    aria-label='Previous Search text'
                  >
                    <span
                      className='e-pv-icon-search e-pv-prev-search-icon'
                      id='container_prev_occurrenceIcon'
                      onClick={previousTextSearch}
                    ></span>
                  </button>
                  <button
                    className='e-btn e-icon-btn e-pv-search-btn e-icons e-chevron-right'
                    id='container_next_occurrence'
                    type='button'
                    disabled
                    aria-label='Next Search text'
                  >
                    <span
                      className='e-pv-icon-search e-pv-next-search-icon'
                      id='container_next_occurrenceIcon'
                      onClick={nextTextSearch}
                    ></span>
                  </button>
                </div>
                <div
                  className='e-pv-match-case-container'
                  id='container_match_case_container'
                >
                  <div className='e-checkbox-wrapper e-wrapper e-pv-match-case'>
                    <label htmlFor='container_match_case'>
                      <input
                        id='container_match_case'
                        type='checkbox'
                        className='e-control e-checkbox e-lib'
                        onClick={checkBoxChanged}
                      />
                      <span
                        className='e-ripple-container'
                        data-ripple='true'
                      ></span>
                      <span id='checkboxSpan' className='e-icons e-frame'></span>
                      <span className='e-label'>
                        {t('pdfViewer.checkBox.matchCase')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            {/* Render the PDF Viewer */}
            <PdfViewerComponent
              id='container'
              ref={(scope) => {
                viewer = scope
              }}
              enableToolbar={false}
              enableNavigationToolbar={false}
              enableAnnotationToolbar={false}
              enableCommentPanel={false}
              documentLoad={documentLoaded}
              created={handleCreated}
              documentLoadFailed={handleDocumentLoadFailed}
              pageChange={onPageChange}
              resourceUrl={`${process.env.REACT_APP_RESOURCE_URL_PDF_VIEWER_SYNCFUSION}`}
              documentPath={url}
              style={{
                display: 'block',
                height: viewerHeight,
                touchAction: 'pan-x pan-y',
                minHeight: '500px'
              }}            >
              <Inject
                services={[
                  Magnification,
                  Navigation,
                  LinkAnnotation,
                  BookmarkView,
                  FormFields,
                  FormDesigner,
                  PageOrganizer,
                  ThumbnailView,
                  Print,
                  TextSelection,
                  TextSearch,
                  Annotation,
                ]}
              />
            </PdfViewerComponent>
          </div>
        </div>
      )}

    </div>
  )

  function onPageChange() {
    currentPageNumber = viewer.currentPageNumber.toString()
    let inputElement = document.getElementById('currentPage')
    inputElement.value = currentPageNumber
    updatePageNavigation()
  }
  function documentLoaded() {
    document.addEventListener('click', checkSearchActive.bind(this))
    var pageCount = document.getElementById('totalPage')
    pageCount.textContent = `${t('of')} ` + viewer.pageCount
    updatePageNavigation()
    let inputElement = document.getElementById('currentPage')
    inputElement.addEventListener('click', currentPageClicked.bind(this))
    inputElement.addEventListener(
      'keypress',
      onCurrentPageBoxKeypress.bind(this)
    )
    inputElement.value = '1'
  }
  function updatePageNavigation() {
    if (viewer.currentPageNumber === 1) {
      toolbar.enableItems(
        document.getElementById('previous_page').parentElement,
        false
      )
      toolbar.enableItems(
        document.getElementById('next_page').parentElement,
        true
      )
    } else if (viewer.currentPageNumber === viewer.pageCount) {
      toolbar.enableItems(
        document.getElementById('previous_page').parentElement,
        true
      )
      toolbar.enableItems(
        document.getElementById('next_page').parentElement,
        false
      )
    } else {
      toolbar.enableItems(
        document.getElementById('previous_page').parentElement,
        true
      )
      toolbar.enableItems(
        document.getElementById('next_page').parentElement,
        true
      )
    }
  }
  function onCurrentPageBoxKeypress(event) {
    let currentPageBox = document.getElementById('currentPage')
    if (
      (event.which < 48 || event.which > 57) &&
      event.which !== 8 &&
      event.which !== 13
    ) {
      event.preventDefault()
      return false
    } else {
      var currentPageNumber = parseInt(currentPageBox.value)
      if (event.which === 13) {
        if (currentPageNumber > 0 && currentPageNumber <= viewer.pageCount) {
          viewer.navigation.goToPage(currentPageNumber)
        } else {
          currentPageBox.value = viewer.currentPageNumber.toString()
        }
      }
      return true
    }
  }

  function currentPageClicked() {
    let currentPage = document.getElementById('currentPage')
    currentPage.select()
  }
  function searchInputKeypressed(event) {
    if (event.key === 'Enter') {
      initiateTextSearch()
    }
  }
  function initiateTextSearch() {
    const textsearchPrevElement = document.getElementById(
      'container_prev_occurrence'
    )
    const textsearchNextElement = document.getElementById(
      'container_next_occurrence'
    )
    var textsearchElement = document.getElementById(
      'container_search_box-icon'
    )
    if (textsearchNextElement && textsearchPrevElement && textsearchElement) {
      textsearchPrevElement.disabled = false
      textsearchNextElement.disabled = false
      textsearchElement.classList.add('e-close')
      textsearchElement.classList.remove('e-search')
      textsearchPrevElement.addEventListener('click', previousTextSearch)
      textsearchNextElement.addEventListener('click', nextTextSearch)
      if (
        searchText !==
        document.getElementById('container_search_input').value ||
        prevMatchCase !== matchCase
      ) {
        viewer.textSearch.cancelTextSearch()
        searchText = document.getElementById('container_search_input').value
        searchActive = true
        viewer.textSearch.searchText(searchText, matchCase)
        prevMatchCase = matchCase
      } else {
        nextTextSearch()
      }
    }
  }
  function checkSearchActive() {
    if (viewer && viewer.textSearchModule && !searchActive) {
      viewer.textSearchModule.clearAllOccurrences()
    }
  }
  function inputChange() {
    viewer.textSearchModule.clearAllOccurrences()
    searchActive = false
    if (document.getElementById('container_search_input').value === '') {
      updateSearchInputIcon(true)
      viewer.textSearch.cancelTextSearch()
      searchText = ''
    }
  }
  function searchClickHandler() {
    var searchBtn = document.getElementById('container_search_box-icon')
    if (searchBtn.classList.contains('e-search')) {
      viewer.textSearch.cancelTextSearch()
      initiateTextSearch()
      updateSearchInputIcon(false)
      searchText = ''
    } else if (searchBtn.classList.contains('e-close')) {
      var searchInput = document.getElementById('container_search_input')
      updateSearchInputIcon(true)
      searchInput.value = ''
      searchInput.focus()
      viewer.textSearch.cancelTextSearch()
      searchText = ''
    }
  }
  function updateSearchInputIcon(isEnable) {
    var searchBtn = document.getElementById('container_search_box-icon')
    if (isEnable) {
      searchBtn.classList.add('e-search')
      searchBtn.classList.remove('e-close')
    } else {
      searchBtn.classList.add('e-close')
      searchBtn.classList.remove('e-search')
    }
  }
  function nextTextSearch() {
    disableInkAnnotation()
    viewer.textSearchModule.searchNext()
    searchActive = true
  }
  function previousTextSearch() {
    disableInkAnnotation()
    viewer.textSearchModule.searchPrevious()
    searchActive = true
  }
  function disableInkAnnotation() {
    if (isInkEnabled) {
      viewer.annotation.setAnnotationMode('None')
      isInkEnabled = false
    }
  }
  function checkBoxChanged(event) {
    const target = event.target
    if (target.checked) {
      const matchcaseElement = document.getElementById('container_match_case')
      if (matchcaseElement) {
        matchcaseElement.checked = true
      }
      matchCase = true
      const checkboxSpanElement = document.getElementById('checkboxSpan')
      if (checkboxSpanElement) {
        checkboxSpanElement.classList.add('e-check')
      }
    } else {
      matchCase = false
      const checkboxSpanElement = document.getElementById('checkboxSpan')
      if (checkboxSpanElement) {
        checkboxSpanElement.classList.remove('e-check')
      }
    }
  }
  function clickHandler(args) {
    switch (args.item.id) {
      case 'previous_page':
        disableInkAnnotation()
        viewer.navigation.goToPreviousPage()
        break
      case 'next_page':
        disableInkAnnotation()
        viewer.navigation.goToNextPage()
        break
      case 'first_page':
        disableInkAnnotation()
        viewer.navigation.goToPage(1)
        document.getElementById('currentPage').value = '1'
        break
      case 'last_page':
        disableInkAnnotation()
        viewer.navigation.goToPage(viewer.pageCount)
        document.getElementById('currentPage').value =
          viewer.pageCount.toString()
        break
      case 'zoom_in':
        {
          disableInkAnnotation()
          viewer.magnification.zoomIn()
          let textSearchToolbarElement =
            document.getElementById('textSearchToolbar')
          if (textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
        }
        break
      case 'zoom_out':
        {
          disableInkAnnotation()
          viewer.magnification.zoomOut()
          let textSearchToolbarElement =
            document.getElementById('textSearchToolbar')
          if (textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
        }
        break
      case 'text_selection_tool':
        {
          disableInkAnnotation()
          viewer.interactionMode = 'TextSelection'
          let textSearchToolbarElement =
            document.getElementById('textSearchToolbar')
          if (textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
        }
        break
      case 'pan_tool':
        {
          disableInkAnnotation()
          viewer.interactionMode = 'Pan'
          let textSearchToolbarElement =
            document.getElementById('textSearchToolbar')
          if (textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
        }
        break
      case 'find_text': {
        disableInkAnnotation()
        let textSearchToolbarElement =
          document.getElementById('textSearchToolbar')
        if (textSearchToolbarElement.style.display === 'block')
          textSearchToolbarElement.style.display = 'none'
        else textSearchToolbarElement.style.display = 'block'
        break
      }
      default:
        break
    }
  }
}

export default PdfViewer